import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';

/**
 * Single source of truth for the scheme's social profiles.
 *
 * The nav, footer and leadership page all render this list, so a new handle
 * only has to be filled in here. Entries still on '#' have no public profile
 * URL yet; components treat non-http hrefs as placeholders (no target=_blank).
 */

/** lucide dropped brand glyphs, so X and TikTok ship as inline SVGs. */
export function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function TikTok({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.78.12V9.79a5.7 5.7 0 0 0-.78-.05 5.69 5.69 0 1 0 5.69 5.69V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.28 4.28 0 0 1-3.25-1.48z" />
    </svg>
  );
}

export interface SocialLink {
  Icon: (props: { className?: string }) => React.ReactNode;
  label: string;
  href: string;
}

export const SOCIALS: SocialLink[] = [
  {
    Icon: Facebook,
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61590461622821',
  },
  { Icon: XLogo, label: 'X', href: 'https://x.com/ISPSug' },
  {
    Icon: Linkedin,
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/informal-workers-social-protection-scheme-isps/',
  },
  { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/ispsuganda/' },
  { Icon: TikTok, label: 'TikTok', href: 'https://www.tiktok.com/@ispsuganda' },
  // No YouTube channel yet — '#' renders the icon without an outbound link.
  { Icon: Youtube, label: 'YouTube', href: '#' },
];

/** Icon per platform key, so CMS rows ("facebook", "x", …) resolve to a glyph. */
export const SOCIAL_ICONS: Record<string, SocialLink['Icon']> = {
  facebook: Facebook,
  x: XLogo,
  linkedin: Linkedin,
  instagram: Instagram,
  tiktok: TikTok,
  youtube: Youtube,
};

/** Human label per platform key, used for the accessible name. */
export const SOCIAL_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  x: 'X',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

/**
 * Resolve the CMS's social rows into renderable links, falling back to the
 * built-in list when nothing is configured. Rows whose platform we have no
 * icon for are dropped rather than rendered as a blank square.
 */
export function resolveSocials(
  rows?: { platform: string; url: string }[] | null,
): SocialLink[] {
  if (!rows || rows.length === 0) return SOCIALS;
  return rows
    .filter((r) => SOCIAL_ICONS[r.platform])
    .map((r) => ({
      Icon: SOCIAL_ICONS[r.platform],
      label: SOCIAL_LABELS[r.platform] ?? r.platform,
      // A blank URL means "no profile yet" — render the icon, but not a link.
      href: r.url?.trim() || '#',
    }));
}

/** Extra props for a real (http) profile link; nothing for '#' placeholders. */
export function externalProps(href: string) {
  return href.startsWith('http')
    ? { target: '_blank' as const, rel: 'noopener noreferrer' }
    : {};
}
